import { createContext, useCallback, useMemo, useState } from 'react';
import { useContextOrThrow } from '@elfsight/lib-utils';
import { noop } from 'es-toolkit';
import type { DefaultComponentProps } from '../types';

type RerenderType = ((callback?: () => void) => void) | undefined;

type LayoutProviderType = {
  rerender: RerenderType;
  setRerender: (func: RerenderType) => void;
  carouselVisibleIndexes: number[];
  setCarouselVisibleIndexes(value: number[]): void;
  carouselRows: number;
  setCarouselRows(value: number): void;
};

const LayoutContext = createContext<LayoutProviderType>({
  rerender: undefined,
  carouselVisibleIndexes: [],
  carouselRows: 0,
  setRerender: noop,
  setCarouselVisibleIndexes: noop,
  setCarouselRows: noop
});

export function LayoutProvider({ children }: DefaultComponentProps) {
  const [carouselVisibleIndexes, setCarouselVisibleIndexes] = useState<
    number[]
  >([]);
  const [carouselRows, setCarouselRows] = useState(0);
  const [rerender, setRerender] = useState<RerenderType>(undefined);

  const setRerenderCallback = useCallback(
    (func: RerenderType) => setRerender(() => func),
    [setRerender]
  );

  const layoutContext = useMemo<LayoutProviderType>(
    () => ({
      carouselVisibleIndexes,
      setCarouselVisibleIndexes,
      carouselRows,
      setCarouselRows,
      rerender,
      setRerender: setRerenderCallback
    }),
    [carouselVisibleIndexes, carouselRows, rerender, setRerenderCallback]
  );

  return (
    <LayoutContext.Provider value={layoutContext}>
      {children}
    </LayoutContext.Provider>
  );
}

export function useLayout() {
  return useContextOrThrow(LayoutContext, 'Layout');
}
